// src/components/surveys/builder/SurveyBuilderCanvas.tsx
import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Save,
  Eye,
  Settings,
  Undo2,
  Redo2,
  FileText,
  GitBranch,
  SkipForward,
  Send,
  Folder,
} from 'lucide-react';
import { Project } from '../../../types/project.types';
import { useSurveyBuilderStore, useSelectedQuestion, useCanUndo, useCanRedo } from '../../../store/surveyBuilderStore';
import { SurveyQuestion, QuestionType } from '../../../types/survey.types';
import QuestionCard from './QuestionCard';
import QuestionEditor from './QuestionEditor';
import QuestionTypeSelector from './QuestionTypeSelector';
import ConditionalLogicEditor from './ConditionalLogicEditor';
import SkipPatternEditor from './SkipPatternEditor';

interface SortableQuestionProps {
  question: SurveyQuestion;
  isSelected: boolean;
  validationErrors?: string[];
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleRequired: () => void;
  isFirst: boolean;
  isLast: boolean;
}

const SortableQuestion: React.FC<SortableQuestionProps> = ({
  question,
  isSelected,
  validationErrors,
  onSelect,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onToggleRequired,
  isFirst,
  isLast,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <QuestionCard
        question={question}
        isSelected={isSelected}
        isDragging={isDragging}
        validationErrors={validationErrors}
        onSelect={onSelect}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onToggleRequired={onToggleRequired}
        isFirst={isFirst}
        isLast={isLast}
        dragHandleProps={listeners}
      />
    </div>
  );
};

type SidePanel = 'editor' | 'logic' | 'skip' | 'settings' | null;

interface SurveyBuilderCanvasProps {
  onSave?: () => Promise<void>;
  onPublish?: () => Promise<void>;
  isPublished?: boolean;
  projects?: Project[];
  isNewSurvey?: boolean;
}

const SurveyBuilderCanvas: React.FC<SurveyBuilderCanvasProps> = ({
  onSave,
  onPublish,
  isPublished,
  projects = [],
  isNewSurvey = false,
}) => {
  const {
    survey,
    selectedQuestionId,
    hasUnsavedChanges,
    isSaving,
    validationErrors,
    isPreviewMode,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    reorderQuestions,
    moveQuestion,
    selectQuestion,
    updateSurveyName,
    updateSurveyProject,
    updateSurveySettings,
    addConditionalRule,
    updateConditionalRule,
    deleteConditionalRule,
    addSkipPattern,
    updateSkipPattern,
    deleteSkipPattern,
    undo,
    redo,
    setPreviewMode,
    validateSurvey,
    setSaving,
    markAsSaved,
  } = useSurveyBuilderStore();

  const selectedQuestion = useSelectedQuestion();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [sidePanel, setSidePanel] = useState<SidePanel>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const questions = survey?.questions || [];
      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderQuestions(oldIndex, newIndex);
      }
    }
  };

  const handleAddQuestion = (type: QuestionType) => {
    addQuestion(type, selectedQuestionId || undefined);
    setSidePanel('editor');
  };

  const handleSave = useCallback(async () => {
    if (!validateSurvey()) {
      return;
    }

    if (onSave) {
      setSaving(true);
      try {
        await onSave();
      } catch (error) {
        console.error('Error saving survey:', error);
      } finally {
        setSaving(false);
      }
    }
  }, [validateSurvey, setSaving, onSave]);

  const handlePublish = useCallback(async () => {
    if (!validateSurvey()) {
      return;
    }

    if (onPublish) {
      setSaving(true);
      try {
        await onPublish();
      } catch (error) {
        console.error('Error publishing survey:', error);
      } finally {
        setSaving(false);
      }
    }
  }, [validateSurvey, setSaving, onPublish]);

  const handleQuestionSelect = (questionId: string) => {
    selectQuestion(questionId);
    setSidePanel('editor');
  };

  if (!survey) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No survey loaded</p>
      </div>
    );
  }

  const questions = survey.questions || [];
  const activeQuestion = questions.find((q) => q.id === activeId);

  return (
    <div className="flex h-full">
      {/* Main canvas */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 sm:py-3 overflow-x-auto">
          <div className="flex items-center justify-between gap-2 min-w-0">
            {/* Left side - Survey name and project */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink">
              {/* Survey name (editable) */}
              <input
                type="text"
                value={survey.name}
                onChange={(e) => updateSurveyName(e.target.value)}
                className="text-base sm:text-lg font-semibold text-gray-900 border-none focus:ring-0 focus:outline-none bg-transparent min-w-0 w-40 sm:w-auto"
                placeholder="Survey name..."
              />

              {/* Project selector */}
              {projects.length > 0 && (
                <div className="flex items-center gap-1 sm:gap-2 px-2 py-1 bg-gray-100 rounded-lg flex-shrink-0">
                  <Folder className="h-4 w-4 text-gray-500 hidden sm:block" />
                  <select
                    value={survey.projectId || ''}
                    onChange={(e) => updateSurveyProject(e.target.value)}
                    className="text-xs sm:text-sm bg-transparent border-none focus:ring-0 focus:outline-none text-gray-700 cursor-pointer pr-4 sm:pr-6 max-w-[120px] sm:max-w-none"
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {hasUnsavedChanges && (
                <span className="text-xs text-orange-500 font-medium whitespace-nowrap hidden sm:inline">
                  Unsaved changes
                </span>
              )}
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Undo/Redo */}
              <button
                onClick={undo}
                disabled={!canUndo}
                className={`p-1.5 sm:p-2 rounded ${
                  canUndo
                    ? 'text-gray-600 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Undo"
              >
                <Undo2 className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={redo}
                disabled={!canRedo}
                className={`p-1.5 sm:p-2 rounded ${
                  canRedo
                    ? 'text-gray-600 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Redo"
              >
                <Redo2 className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>

              <div className="w-px h-5 sm:h-6 bg-gray-300 mx-1" />

              {/* Panel toggles */}
              <button
                onClick={() => setSidePanel(sidePanel === 'logic' ? null : 'logic')}
                className={`p-1.5 sm:p-2 rounded ${
                  sidePanel === 'logic'
                    ? 'bg-indigo-100 text-indigo-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Conditional Logic"
              >
                <GitBranch className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={() => setSidePanel(sidePanel === 'skip' ? null : 'skip')}
                className={`p-1.5 sm:p-2 rounded ${
                  sidePanel === 'skip'
                    ? 'bg-orange-100 text-orange-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Skip Patterns"
              >
                <SkipForward className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={() => setSidePanel(sidePanel === 'settings' ? null : 'settings')}
                className={`p-1.5 sm:p-2 rounded ${
                  sidePanel === 'settings'
                    ? 'bg-gray-200 text-gray-800'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Survey Settings"
              >
                <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>

              <div className="w-px h-5 sm:h-6 bg-gray-300 mx-1" />

              {/* Preview */}
              <button
                onClick={() => setPreviewMode(!isPreviewMode)}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded text-sm ${
                  isPreviewMode
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Preview</span>
              </button>

              {/* Save */}
              <button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded text-sm ${
                  isSaving || !hasUnsavedChanges
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                <Save className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
              </button>

              {/* Publish */}
              <button
                onClick={handlePublish}
                disabled={isSaving || hasUnsavedChanges || isPublished}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded text-sm ${
                  isSaving || hasUnsavedChanges
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : isPublished
                    ? 'bg-green-100 text-green-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                title={hasUnsavedChanges ? 'Save changes first' : isPublished ? 'Survey is published' : 'Publish survey'}
              >
                <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">{isPublished ? 'Published' : 'Publish'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Canvas content */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-3 sm:p-6">
          {/* Add question button (top) */}
          <div className="flex justify-center mb-6">
            <QuestionTypeSelector onSelect={handleAddQuestion} />
          </div>

          {/* Questions list with drag-drop */}
          {questions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white border-2 border-dashed border-gray-300 rounded-lg">
              <FileText className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No questions yet
              </h3>
              <p className="text-gray-500 mb-4">
                Add your first question using the button above
              </p>
              <QuestionTypeSelector onSelect={handleAddQuestion} position="inline" />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={questions.map((q) => q.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4 max-w-2xl lg:max-w-3xl mx-auto">
                  {questions.map((question, index) => (
                    <SortableQuestion
                      key={question.id}
                      question={question}
                      isSelected={selectedQuestionId === question.id}
                      validationErrors={validationErrors[question.id]}
                      onSelect={() => handleQuestionSelect(question.id)}
                      onDelete={() => deleteQuestion(question.id)}
                      onDuplicate={() => duplicateQuestion(question.id)}
                      onMoveUp={() => moveQuestion(question.id, 'up')}
                      onMoveDown={() => moveQuestion(question.id, 'down')}
                      onToggleRequired={() =>
                        updateQuestion(question.id, { required: !question.required })
                      }
                      isFirst={index === 0}
                      isLast={index === questions.length - 1}
                    />
                  ))}
                </div>
              </SortableContext>

              <DragOverlay>
                {activeQuestion && (
                  <QuestionCard
                    question={activeQuestion}
                    isSelected={false}
                    isDragging
                    onSelect={() => {}}
                    onDelete={() => {}}
                    onDuplicate={() => {}}
                    onMoveUp={() => {}}
                    onMoveDown={() => {}}
                    onToggleRequired={() => {}}
                    isFirst={false}
                    isLast={false}
                  />
                )}
              </DragOverlay>
            </DndContext>
          )}

          {/* Summary stats */}
          {questions.length > 0 && (
            <div className="mt-8 text-center text-sm text-gray-500">
              {questions.length} question{questions.length !== 1 ? 's' : ''} •{' '}
              {questions.filter((q) => q.required).length} required •{' '}
              {survey.conditionalLogic.length} logic rules •{' '}
              {survey.skipPatterns.length} skip patterns
            </div>
          )}
        </div>
      </div>

      {/* Side panel */}
      {sidePanel && (
        <div className="w-72 lg:w-80 xl:w-96 border-l border-gray-200 bg-white overflow-hidden flex flex-col flex-shrink-0">
          {sidePanel === 'editor' && selectedQuestion && (
            <QuestionEditor
              question={selectedQuestion}
              onUpdate={(updates) => updateQuestion(selectedQuestion.id, updates)}
              onClose={() => {
                selectQuestion(null);
                setSidePanel(null);
              }}
              validationErrors={validationErrors[selectedQuestion.id]}
            />
          )}

          {sidePanel === 'logic' && (
            <div className="p-4 overflow-y-auto">
              <ConditionalLogicEditor
                questions={questions}
                rules={survey.conditionalLogic}
                onAddRule={addConditionalRule}
                onUpdateRule={updateConditionalRule}
                onDeleteRule={deleteConditionalRule}
              />
            </div>
          )}

          {sidePanel === 'skip' && (
            <div className="p-4 overflow-y-auto">
              <SkipPatternEditor
                questions={questions}
                patterns={survey.skipPatterns}
                onAddPattern={addSkipPattern}
                onUpdatePattern={updateSkipPattern}
                onDeletePattern={deleteSkipPattern}
              />
            </div>
          )}

          {sidePanel === 'settings' && (
            <div className="p-4 overflow-y-auto space-y-6">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Survey Settings
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={survey.description || ''}
                  onChange={(e) =>
                    useSurveyBuilderStore.getState().updateSurveyDescription(e.target.value)
                  }
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Describe the purpose of this survey..."
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Allow Back Navigation
                    </label>
                    <p className="text-xs text-gray-500">
                      Let respondents go back to previous questions
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      updateSurveySettings({ allowBack: !survey.settings.allowBack })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      survey.settings.allowBack ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        survey.settings.allowBack ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Show Progress Bar
                    </label>
                    <p className="text-xs text-gray-500">
                      Display completion progress to respondents
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      updateSurveySettings({ showProgress: !survey.settings.showProgress })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      survey.settings.showProgress ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        survey.settings.showProgress ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Randomize Questions
                    </label>
                    <p className="text-xs text-gray-500">
                      Show questions in random order
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      updateSurveySettings({
                        randomizeQuestions: !survey.settings.randomizeQuestions,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      survey.settings.randomizeQuestions ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        survey.settings.randomizeQuestions ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Submit Button Text
                </label>
                <input
                  type="text"
                  value={survey.settings.submitButtonText || 'Submit'}
                  onChange={(e) =>
                    updateSurveySettings({ submitButtonText: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Completion Message
                </label>
                <textarea
                  value={survey.settings.completionMessage || ''}
                  onChange={(e) =>
                    updateSurveySettings({ completionMessage: e.target.value })
                  }
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Thank you for completing the survey!"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyBuilderCanvas;
